import React from 'react';
import { Modal, Box, IconButton, Typography, Paper, Fade, CircularProgress } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { fileTypeMapping, getFileIconAndColor } from '../utils';
import { useTheme } from "../../style/theme";

const FilePreviewModal = ({ open, handleClose, file, userId, setfilePreviewSelectedFile, setfilePreviewModalOpen }) => {
  const [isLoading, setIsLoading] = React.useState(true);
  const [imgError, setImgError] = React.useState(false);

  const extension = file?.name?.split(".").pop().toLowerCase() || '';
  const isImage = fileTypeMapping.Image.includes(extension);
  const { mode } = useTheme();

  const handleCloseModal = () => {
    setfilePreviewModalOpen(false);
    setfilePreviewSelectedFile(null);
    setIsLoading(true);
    setImgError(false);
  };

  const handleImageLoad = () => {
    setIsLoading(false);
  };

  const handleImageError = () => {
    setIsLoading(false);
    setImgError(true);
  };

  return (
    <Modal
      open={open}
      onClose={handleCloseModal}
      closeAfterTransition
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <Fade in={open} timeout={300}>
        <Paper
        sx={{
          width: '80vw',
          height: '80vh',
          bgcolor: 'background.paper',
          borderRadius: '16px',
          boxShadow: 24,
          overflow: 'hidden',
          position: 'relative',
          display: 'flex',
          flexDirection: 'column',
        }}
        >
          {/* Header */}
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              p: 2,
              bgcolor: 'background.default',
              borderBottom: '1px solid',
              borderColor: 'divider',
            }}
          >
            <Typography variant="h6" noWrap sx={{ maxWidth: '80%' }}>
              {file?.name}
            </Typography>
            <IconButton onClick={handleCloseModal} sx={{ color: 'text.secondary' }}>
              <CloseIcon />
            </IconButton>
          </Box>

          {/* Content */}
          <Box
            sx={{
              flex: 1,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              p: 3,
              overflow: 'auto',
              position: 'relative',
            }}
          >
            {isLoading && (
              <Box 
                sx={{ 
                  position: 'absolute', 
                  top: '50%', 
                  left: '50%', 
                  transform: 'translate(-50%, -50%)',
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center'
                }}
              >
                <CircularProgress 
                  color={mode === 'dark' ? "inherit" : "primary"} 
                />
              </Box>
            )}

            {isImage ? (
              <>
                <Box
                  sx={{
                    display: isLoading ? 'none' : 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: '100%',
                    height: '100%',
                    textAlign: 'center',
                  }}
                >
                  {imgError ? (
                    <Typography color="error">Failed to load image</Typography>
                  ) : (
                    <img
                      src={`${process.env.REACT_APP_NODE_FILE_MANAGER_SERVER}/api/fs/access/${file._id}?userId=${userId}`}
                      alt={file?.name}
                      style={{
                        maxWidth: '100%',
                        maxHeight: '100%',
                        objectFit: 'contain',
                        borderRadius: '8px',
                      }}
                      onLoad={handleImageLoad}
                      onError={handleImageError}
                    />
                  )}
                </Box>
              </>
            ) : (
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: 2,
                }}
              >
                {(() => {
                  const { icon } = getFileIconAndColor({
                    type: "file",
                    extension,
                    isShortcut: file?.isShortcut,
                    overrideColor: file?.color,
                    fontSize: 100,
                    mode: mode,
                  });
                  return (
                    <Box
                      sx={{
                        fontSize: 100,
                        color: 'primary.main',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      {icon}
                    </Box>
                  );
                })()}
                <Typography variant="subtitle1" color="text.secondary">
                  {extension.toUpperCase()} File
                </Typography>
              </Box>
            )}
          </Box>

          {/* Footer */}
          <Box
            sx={{
              p: 2,
              bgcolor: 'background.default',
              borderTop: '1px solid',
              borderColor: 'divider',
              textAlign: 'center',
            }}
          >
            <Typography variant="caption" color="text.secondary">
              {file?.size} • {new Date(file?.createdAt).toLocaleDateString()}
            </Typography>
          </Box>
        </Paper>
      </Fade>
    </Modal>
  );
};

export default FilePreviewModal;